import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, faUsers, faClock, faPlay, faRobot, faBrain, faChartLine, 
  faCode, faProjectDiagram, faCloud, faCheckCircle, faDatabase, 
  faServer, faLaptopCode, faCertificate, faBriefcase 
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGithub, faTwitter, faMedium } from '@fortawesome/free-brands-svg-icons';
import './HeroSection.css';

const DataScienceCourse = () => {
  const [showModal, setShowModal] = useState(false);
  const [formCourseName] = useState('Data Science');
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => setShowModal(true);
  const closeModal = useCallback(() => {
    setShowModal(false);
    setFormData({ fullName: '', mobileNumber: '', email: '' });
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobileNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const { fullName, mobileNumber, email } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

    if (!fullName || !emailRegex.test(email) || mobileNumber.length !== 10) {
      alert('Please fill in valid details.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.dikshasea.in/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, course: formCourseName }),
      });

      const result = await response.json();
      if (response.ok) {
        alert('Enrollment successful!');
        closeModal();
      } else {
        alert(result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submission error:', err);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="hero-title">
                Master <span className="hero-highlight">Data Science</span>
              </h1>
              <p className="hero-subtitle">
                Unlock the power of data with our comprehensive Data Scientist course. 
                Designed for beginners and professionals, this course equips you with 
                cutting-edge tools and techniques to analyze, model, and visualize data.
              </p>
             
              <div className="course-meta">
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <div className="fw-bold">4.8/5.0</div>
                    <small className="text-muted">(3,400+ reviews)</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <div className="fw-bold">10,000+</div>
                    <small className="text-muted">Students</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="fw-bold">50 hours</div>
                    <small className="text-muted">Content</small>
                  </div>
                </div>
              </div>
             
              <div className="d-flex flex-wrap gap-3">
                <button onClick={openModal} className="btn btn-primary btn-lg px-4">
                  Enroll Now
                </button>
              </div>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ratio ratio-16x9">
                <img
                  src="/subcourses-images/data-science.jpg"
                  alt="Data Scientist Course"
                  className="img-fluid rounded-3 shadow"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Modal */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment Visual"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">{formCourseName}</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="tel"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark me-2"
                      onClick={closeModal}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container">
        <div className="course-details">
          <div className="course-content">
            <section className="about-course">
              <h2 className="section-title">About This Course</h2>
              <p>This Data Science course transforms you into a skilled data scientist. You'll learn to gather, clean, and analyze data, build machine learning models, and present actionable insights.</p>
              <p>Work on real-world projects and master industry tools like Python, Pandas, Scikit-learn, and more to become job-ready.</p>
            </section>
 
            <section className="curriculum">
              <h2 className="section-title">Curriculum</h2>
 
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faDatabase} className="me-2" /> Module 1: Python & Statistics</h3>
                <p>Start with Python programming and statistical foundations.</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Python basics, NumPy, Pandas</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Data visualization with Matplotlib & Seaborn</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Descriptive & inferential statistics</div>
                </div>
              </div>
 
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faServer} className="me-2" /> Module 2: Data Wrangling & EDA</h3>
                <p>Explore techniques to clean and understand data.</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Missing data handling</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Feature engineering</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Exploratory data analysis</div>
                </div>
              </div>
 
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCloud} className="me-2" /> Module 3: Machine Learning</h3>
                <p>Train predictive models and evaluate performance.</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Regression and classification</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Tree-based models, KNN, SVM</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model tuning and validation</div>
                </div>
              </div>
 
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCode} className="me-2" /> Module 4: Deployment</h3>
                <p>Learn to deploy your ML models into production.</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Building APIs using Flask/FastAPI</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Deployment on cloud (AWS/GCP)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model monitoring & maintenance</div>
                </div>
              </div>
 
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faProjectDiagram} className="me-2" /> Module 5: Capstone Project</h3>
                <p>Apply your skills to solve a real-world problem.</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Data collection to deployment</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> End-to-end model building</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Final presentation</div>
                </div>
              </div>
            </section>
          </div>
 
          <div className="course-sidebar">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="info-content">
                <h4>Duration</h4>
                <p>12 Weeks (4–5 hrs/week)</p>
              </div>
            </div>
 
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <div className="info-content">
                <h4>Level</h4>
                <p>Beginner to Advanced</p>
              </div>
            </div>
 
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <div className="info-content">
                <h4>Projects</h4>
                <p>5+ Real-world Projects</p>
              </div>
            </div>
 
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="info-content">
                <h4>Prerequisites</h4>
                <p>Basic Python & Math</p>
              </div>
            </div>
 
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="info-content">
                <h4>Career Support</h4>
                <p>Mock interviews, Resume reviews</p>
              </div>
            </div>
 
            <button onClick={openModal} className="enroll-button" style={{display: 'block', textAlign: 'center', marginTop: '2rem'}}>
              Enroll Now
            </button>
          </div>
        </div>
        {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">{formCourseName}</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-outline-dark me-2" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Submit</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
    
  );
};

export default DataScienceCourse;